import React, { useState, useEffect } from 'react';
import { 
  BarChart3, 
  TrendingUp, 
  History,
  Play,
  RefreshCw,
  Info,
  DollarSign,
  Target,
  Zap,
  ChevronRight,
  AlertTriangle,
  CheckCircle,
  Layers,
  ArrowUpRight,
  ArrowDownRight,
  Minus,
  Lightbulb,
  Calculator,
  FileText,
  Users,
  Clock,
  Award,
  Scale,
  Wrench,
  Plus,
  ChevronDown,
  ChevronUp,
  Code
} from 'lucide-react';
import { api } from '../../utils/api';
import { toast } from 'sonner';

interface TornadoDataItem {
  variable: string;
  low_value: number;
  high_value: number;
  low_outcome: number;
  high_outcome: number;
  swing: number;
  swing_pct: number;
  rank: number;
  base_value: number;
  direction: string;
}

interface TopDriver {
  rank: number;
  variable: string;
  swing: number;
  swing_pct: number;
  elasticity: number;
  direction: string;
  impact_assessment: string;
  recommendation: string;
}

interface Insight {
  type: string;
  priority: number;
  title: string;
  description: string;
  recommendation: string;
  icon: string;
}

interface InteractionEffect {
  variable_1: string;
  variable_2: string;
  combined_effect: number;
  individual_sum: number;
  interaction_strength: number;
  synergy_type: string;
}

interface SensitivityResult {
  success: boolean;
  analysis_id: string;
  base_outcome: number;
  tornado_data: TornadoDataItem[];
  top_drivers: TopDriver[];
  insights: Insight[];
  interaction_effects: InteractionEffect[];
  elasticities: Record<string, number>;
  visualization_data: {
    tornado: any[];
    spider: any[];
    max_swing: number;
  };
}

interface HistoricalResult {
  success: boolean;
  current_position: {
    recovery_rate: number | null;
    percentile_rank: number | null;
    position_label: string | null;
    position_description: string | null;
  };
  benchmarks: {
    industry: string;
    event_type: string;
    sample_size: number;
    adjusted: {
      avg_recovery_rate: number;
      median_recovery_rate: number;
      p25_recovery_rate: number;
      p75_recovery_rate: number;
      p90_recovery_rate: number;
    };
  };
  percentile_values: {
    p25: number;
    p50: number;
    p75: number;
    p90: number;
    avg: number;
  };
  gap_analysis: {
    vs_median: number | null;
    vs_p75: number | null;
    potential_upside: number;
  };
  recommendations: Array<{
    priority: number;
    category: string;
    title: string;
    recommendation: string;
    rationale: string;
  }>;
}

interface WhatIfTemplate {
  id: string;
  name: string;
  description: string;
  scenario_type: string;
  modification_count: number;
  total_effort_cost: number;
  total_effort_hours: number;
  total_impact_pct: number;
  avg_success_probability: number;
}

interface WhatIfScenario {
  scenario_id: string;
  name: string;
  description: string;
  scenario_type: string;
  modification_count: number;
  modifications: Array<{
    name: string;
    description: string;
    effort_level: string;
    effort_cost: number;
    effort_hours: number;
    settlement_impact_pct: number;
    success_probability: number;
  }>;
  projected_settlement: number;
  total_effort_cost: number;
  total_effort_hours: number;
  roi: number;
  net_benefit: number;
  risk_adjusted_benefit: number;
}

interface WhatIfResult {
  success: boolean;
  comparison_id: string;
  base_settlement: number;
  scenarios: WhatIfScenario[];
  comparison_summary: {
    best_roi: { scenario: string; roi: number };
    best_net_benefit: { scenario: string; benefit: number };
    recommended: { scenario: string; rationale: string };
  };
  recommended_breakdown: {
    scenario_name: string;
    gross_benefit: number;
    total_cost: number;
    net_benefit: number;
    roi_percentage: number;
    modification_breakdown: Array<{
      name: string;
      effort_cost: number;
      dollar_impact: number;
      risk_adjusted_impact: number;
      individual_roi: number;
    }>;
  };
}

interface Modification {
  id: string;
  template_id: string;
  template_name: string;
  name: string;
  description: string;
  effort_level: string;
  effort_cost: number;
  effort_hours: number;
  settlement_impact_pct: number;
  success_probability: number;
  category: string;
}

interface LitigationResult {
  success: boolean;
  settlement_path: {
    offer_amount: number;
    npv: number;
  };
  litigation_path: {
    expected_value_npv: number;
    total_litigation_cost: number;
    time_to_resolution_months: number;
    win_probability: number;
    scenario_breakdown: {
      win_no_appeal: { probability: number; value: number; npv: number };
      win_appeal_upheld: { probability: number; value: number; npv: number };
      win_appeal_reversed: { probability: number; value: number; npv: number };
      lose_trial: { probability: number; value: number; npv: number };
    };
  };
  comparison: {
    settlement_advantage: number;
    advantage_percentage: number;
    break_even_settlement: number;
    recommended_counter_offer: number;
  };
  recommendation: {
    action: string;
    rationale: string;
    confidence: string;
  };
  litigation_cost_breakdown: {
    attorney_fees: number;
    expert_witnesses: number;
    court_costs: number;
    discovery_costs: number;
    total: number;
  };
}

const formatCurrency = (value: number) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
};

const getImpactColor = (assessment: string) => {
  switch (assessment) {
    case 'critical': return 'text-red-600 bg-red-100';
    case 'high': return 'text-orange-600 bg-orange-100';
    case 'moderate': return 'text-yellow-600 bg-yellow-100';
    default: return 'text-gray-600 bg-gray-100';
  }
};

const getPositionColor = (label: string | null) => {
  switch (label) {
    case 'Excellent': return 'text-green-600 bg-green-100 border-green-200';
    case 'Above Average': return 'text-blue-600 bg-blue-100 border-blue-200';
    case 'Average': return 'text-yellow-600 bg-yellow-100 border-yellow-200';
    case 'Below Average': return 'text-orange-600 bg-orange-100 border-orange-200';
    case 'Poor': return 'text-red-600 bg-red-100 border-red-200';
    default: return 'text-gray-600 bg-gray-100 border-gray-200';
  }
};

interface SettlementIntelligenceDashboardProps {
  initialTab?: 'sensitivity' | 'historical' | 'whatif' | 'builder' | 'litigation';
  caseId?: string;
  claimValue?: number;
  embedded?: boolean;
  businessType?: string;
  eventType?: string;
}

export default function SettlementIntelligenceDashboard({
  initialTab = 'sensitivity',
  caseId: propCaseId,
  claimValue: propClaimValue,
  embedded = false,
  businessType: propBusinessType,
  eventType: propEventType
}: SettlementIntelligenceDashboardProps) {
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'sensitivity' | 'historical' | 'whatif' | 'builder' | 'litigation'>(initialTab);
  
  // Case Integration state
  const [availableCases, setAvailableCases] = useState<any[]>([]);
  const [selectedCaseId, setSelectedCaseId] = useState<string>('');
  const [caseData, setCaseData] = useState<any>(null);
  const [loadingCase, setLoadingCase] = useState(false);

  // Sensitivity state
  const [sensitivityResult, setSensitivityResult] = useState<SensitivityResult | null>(null);
  const [claimValue, setClaimValue] = useState<string>('500000');
  const [perturbationPct, setPerturbationPct] = useState<string>('20');
  
  // Historical state
  const [historicalResult, setHistoricalResult] = useState<HistoricalResult | null>(null);
  const [historicalClaim, setHistoricalClaim] = useState<string>('500000');
  const [settlementValue, setSettlementValue] = useState<string>('');
  const [industry, setIndustry] = useState<string>('general');
  const [eventType, setEventType] = useState<string>('general');

  // What-If state
  const [whatifTemplates, setWhatifTemplates] = useState<WhatIfTemplate[]>([]);
  const [whatifResult, setWhatifResult] = useState<WhatIfResult | null>(null);
  const [whatifClaim, setWhatifClaim] = useState<string>('500000');
  const [selectedTemplates, setSelectedTemplates] = useState<string[]>([]);

  // Build Your Own Strategy state
  const [allModifications, setAllModifications] = useState<Modification[]>([]);
  const [modsByCategory, setModsByCategory] = useState<Record<string, Modification[]>>({});
  const [selectedMods, setSelectedMods] = useState<string[]>([]);
  const [builderClaim, setBuilderClaim] = useState<string>('500000');
  const [builderResult, setBuilderResult] = useState<any>(null);

  // Litigation Economics state
  const [litigationResult, setLitigationResult] = useState<LitigationResult | null>(null);
  const [currentOffer, setCurrentOffer] = useState<string>('300000');
  const [litigationClaim, setLitigationClaim] = useState<string>('500000');
  const [complexity, setComplexity] = useState<string>('medium');
  const [docStrength, setDocStrength] = useState<string>('medium');
  const [feeArrangement, setFeeArrangement] = useState<string>('contingency');

  // Calculation details visibility state
  const [showLitigationCalcs, setShowLitigationCalcs] = useState<boolean>(false);
  const [showBuilderCalcs, setShowBuilderCalcs] = useState<boolean>(false);
  const [showWhatifCalcs, setShowWhatifCalcs] = useState<boolean>(false);

  // Update tab when initialTab prop changes (for embedded mode)
  useEffect(() => {
    if (initialTab) {
      setActiveTab(initialTab);
    }
  }, [initialTab]);

  // Load cases on mount (skip if embedded with caseId)
  useEffect(() => {
    if (embedded && propCaseId) {
      // In embedded mode with a case ID, just load that case
      loadCaseData(propCaseId);
      setSelectedCaseId(propCaseId);
      return;
    }
    
    const loadCases = async () => {
      try {
        const data = await api.get('/api/v1/settlement-intelligence/cases?limit=50');
        if (data.cases) {
          setAvailableCases(data.cases);
        }
      } catch (error) {
        console.error('Failed to load cases:', error);
      }
    };
    loadCases();
  }, [embedded, propCaseId]);

  // Initialize claim values from props
  useEffect(() => {
    if (propClaimValue && propClaimValue > 0) {
      const claimStr = propClaimValue.toString();
      setClaimValue(claimStr);
      setHistoricalClaim(claimStr);
      setWhatifClaim(claimStr);
      setBuilderClaim(claimStr);
      setLitigationClaim(claimStr);
    }
  }, [propClaimValue]);

  // Initialize industry/eventType from props (case context)
  useEffect(() => {
    if (propBusinessType) setIndustry(propBusinessType);
  }, [propBusinessType]);

  useEffect(() => {
    if (propEventType) setEventType(propEventType);
  }, [propEventType]);

  // Load case data when selected
  const loadCaseData = async (reportId: string) => {
    if (!reportId) {
      setCaseData(null);
      return;
    }
    
    setLoadingCase(true);
    try {
      const data = await api.get(`/api/v1/settlement-intelligence/case/${reportId}`);
      setCaseData(data);
      
      // Auto-populate all fields from case data
      const claimVal = data.settlement_values?.claim_value?.toString() || '500000';
      setClaimValue(claimVal);
      setHistoricalClaim(claimVal);
      setWhatifClaim(claimVal);
      setBuilderClaim(claimVal);
      setLitigationClaim(claimVal);
      
      // Set litigation-specific values
      if (data.litigation_inputs) {
        setCurrentOffer(data.litigation_inputs.current_offer?.toString() || '');
        setDocStrength(data.litigation_inputs.documentation_strength || 'medium');
        setComplexity(data.litigation_inputs.complexity || 'medium');
      }
      
      // Set historical comparison values
      if (data.historical_inputs) {
        setIndustry(data.historical_inputs.industry || 'general');
        setEventType(data.historical_inputs.event_type || 'general');
      }
      
      toast.success(`Loaded case: ${data.case_context?.claimant_name || data.case_context?.client_name || data.case_context?.business_name || reportId}`);
    } catch (error) {
      console.error('Failed to load case data:', error);
      toast.error('Failed to load case data');
    } finally {
      setLoadingCase(false);
    }
  };

  // Load what-if templates and modifications on mount
  useEffect(() => {
    const loadData = async () => {
      try {
        const [templatesData, modsData] = await Promise.all([
          api.get('/api/v1/settlement-intelligence/whatif/templates'),
          api.get('/api/v1/settlement-intelligence/whatif/modifications')
        ]);
        setWhatifTemplates(templatesData.templates || []);
        setAllModifications(modsData.modifications || []);
        setModsByCategory(modsData.by_category || {});
      } catch (error) {
        console.error('Failed to load data:', error);
      }
    };
    loadData();
  }, []);

  const runSensitivityAnalysis = async () => {
    const claim = parseFloat(claimValue);
    if (isNaN(claim) || claim <= 0) {
      toast.error('Please enter a valid claim value');
      return;
    }

    setLoading(true);
    try {
      const data = await api.post('/api/v1/settlement-intelligence/sensitivity/quick', {
        claim_value: claim,
        perturbation_pct: parseFloat(perturbationPct) || 20
      });
      setSensitivityResult(data);
      toast.success('Sensitivity analysis complete!');
    } catch (error) {
      console.error('Sensitivity analysis error:', error);
      toast.error('Analysis failed');
    } finally {
      setLoading(false);
    }
  };

  const runHistoricalComparison = async () => {
    const claim = parseFloat(historicalClaim);
    if (isNaN(claim) || claim <= 0) {
      toast.error('Please enter a valid claim value');
      return;
    }

    setLoading(true);
    try {
      const settlement = settlementValue ? parseFloat(settlementValue) : undefined;
      const data = await api.post('/api/v1/settlement-intelligence/historical/compare', {
        claim_value: claim,
        settlement_value: settlement,
        industry: industry,
        event_type: eventType,
        loss_period_months: 12
      });
      setHistoricalResult(data);
      toast.success('Historical comparison complete!');
    } catch (error) {
      console.error('Historical comparison error:', error);
      toast.error('Comparison failed');
    } finally {
      setLoading(false);
    }
  };

  const runWhatIfAnalysis = async () => {
    const claim = parseFloat(whatifClaim);
    if (isNaN(claim) || claim <= 0) {
      toast.error('Please enter a valid claim value');
      return;
    }

    if (selectedTemplates.length === 0) {
      toast.error('Please select at least one scenario');
      return;
    }

    setLoading(true);
    try {
      const data = await api.post('/api/v1/settlement-intelligence/whatif/analyze', {
        base_settlement: claim,
        template_ids: selectedTemplates
      });
      setWhatifResult(data);
      toast.success('What-If analysis complete!');
    } catch (error) {
      console.error('What-If analysis error:', error);
      toast.error('Analysis failed');
    } finally {
      setLoading(false);
    }
  };

  const toggleTemplate = (templateId: string) => {
    setSelectedTemplates(prev => 
      prev.includes(templateId) 
        ? prev.filter(t => t !== templateId)
        : [...prev, templateId]
    );
  };

  const getScenarioTypeIcon = (type: string) => {
    switch (type) {
      case 'documentation': return FileText;
      case 'expert_witness': return Users;
      case 'time_extension': return Clock;
      case 'negotiation_strategy': return Target;
      case 'policy_interpretation': return Award;
      default: return Lightbulb;
    }
  };

  const getRoiColor = (roi: number) => {
    if (roi > 200) return 'text-green-600 bg-green-100';
    if (roi > 100) return 'text-blue-600 bg-blue-100';
    if (roi > 50) return 'text-yellow-600 bg-yellow-100';
    if (roi > 0) return 'text-orange-600 bg-orange-100';
    return 'text-red-600 bg-red-100';
  };

  // Build Your Own Strategy function
  const runBuildStrategy = async () => {
    const claim = parseFloat(builderClaim);
    if (isNaN(claim) || claim <= 0) {
      toast.error('Please enter a valid claim value');
      return;
    }
    if (selectedMods.length === 0) {
      toast.error('Please select at least one modification');
      return;
    }

    setLoading(true);
    try {
      const data = await api.post('/api/v1/settlement-intelligence/whatif/build-strategy', {
        base_settlement: claim,
        modification_ids: selectedMods
      });
      setBuilderResult(data);
      toast.success('Custom strategy built!');
    } catch (error) {
      console.error('Build strategy error:', error);
      toast.error('Failed to build strategy');
    } finally {
      setLoading(false);
    }
  };

  const toggleMod = (modId: string) => {
    setSelectedMods(prev =>
      prev.includes(modId)
        ? prev.filter(m => m !== modId)
        : [...prev, modId]
    );
  };

  // Litigation Economics function
  const runLitigationAnalysis = async () => {
    const offer = parseFloat(currentOffer);
    const claim = parseFloat(litigationClaim);
    if (isNaN(offer) || offer <= 0 || isNaN(claim) || claim <= 0) {
      toast.error('Please enter valid offer and claim values');
      return;
    }

    setLoading(true);
    try {
      const data = await api.post('/api/v1/settlement-intelligence/litigation/analyze', {
        current_offer: offer,
        claim_value: claim,
        complexity: complexity,
        documentation_strength: docStrength,
        fee_arrangement: feeArrangement
      });
      setLitigationResult(data);
      toast.success('Litigation analysis complete!');
    } catch (error) {
      console.error('Litigation analysis error:', error);
      toast.error('Analysis failed');
    } finally {
      setLoading(false);
    }
  };

  const getRecommendationStyle = (action: string) => {
    switch (action) {
      case 'accept': return 'bg-green-500 text-white';
      case 'negotiate': return 'bg-yellow-500 text-white';
      case 'reject_litigate': return 'bg-red-500 text-white';
      default: return 'bg-gray-500 text-white';
    }
  };

  // Tornado chart renderer
  const renderTornadoChart = (data: TornadoDataItem[], baseOutcome: number, maxSwing: number) => {
    if (!data || data.length === 0) return null;
    
    return (
      <div className="space-y-2">
        {data.map((item, idx) => {
          const lowDeviation = item.low_outcome - baseOutcome;
          const highDeviation = item.high_outcome - baseOutcome;
          const maxDeviation = maxSwing / 2;
          
          // Calculate positions (centered at 50%)
          const lowPos = Math.max(0, 50 + (lowDeviation / maxDeviation) * 45);
          const highPos = Math.min(100, 50 + (highDeviation / maxDeviation) * 45);
          
          return (
            <div key={idx} className="flex items-center gap-3">
              <div className="w-32 text-right text-sm font-medium text-gray-700 truncate">
                {item.variable}
              </div>
              <div className="flex-1 h-8 bg-gray-100 rounded relative overflow-hidden">
                {/* Center line */}
                <div className="absolute left-1/2 top-0 bottom-0 w-px bg-gray-400" />
                
                {/* Bar */}
                <div 
                  className={`absolute top-1 bottom-1 rounded ${
                    item.direction === 'positive' ? 'bg-blue-500' : 'bg-blue-400'
                  }`}
                  style={{
                    left: `${Math.min(lowPos, highPos)}%`,
                    width: `${Math.abs(highPos - lowPos)}%`
                  }}
                />
                
                {/* Labels */}
                <div 
                  className="absolute top-1/2 -translate-y-1/2 text-xs text-gray-600"
                  style={{ left: `${Math.min(lowPos, highPos) - 8}%` }}
                >
                  {formatCurrency(item.low_outcome)}
                </div>
                <div 
                  className="absolute top-1/2 -translate-y-1/2 text-xs text-gray-600"
                  style={{ left: `${Math.max(lowPos, highPos) + 2}%` }}
                >
                  {formatCurrency(item.high_outcome)}
                </div>
              </div>
              <div className="w-24 text-right">
                <span className={`text-sm font-medium ${
                  item.swing_pct > 20 ? 'text-red-600' : 
                  item.swing_pct > 10 ? 'text-orange-600' : 'text-gray-600'
                }`}>
                  {formatCurrency(item.swing)}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className={embedded ? "p-4" : "p-6 max-w-7xl mx-auto"}>
      {/* Header - Hide in embedded mode */}
      {!embedded && (
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center">
              <BarChart3 className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Settlement Intelligence</h1>
              <p className="text-gray-500">Advanced sensitivity analysis, benchmarking, and scenario planning</p>
            </div>
          </div>
          
          {/* Case Selector */}
          <div className="flex items-center gap-3">
            <div className="min-w-[280px]">
              <label className="block text-xs font-medium text-gray-500 mb-1">Load from Case</label>
              <select
                value={selectedCaseId}
                onChange={(e) => {
                  setSelectedCaseId(e.target.value);
                  loadCaseData(e.target.value);
                }}
                disabled={loadingCase}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                data-testid="case-selector"
              >
                <option value="">-- Select a case to auto-fill --</option>
                {availableCases.map((c: any) => (
                  <option key={c.id || c._id} value={c.id || c._id}>
                    {c.display_name || c.claimant_name || c.client_name || 'Unknown'} - {formatCurrency(c.result?.final_claim_amount || 0)}
                  </option>
                ))}
              </select>
            </div>
            {loadingCase && <RefreshCw className="w-5 h-5 text-blue-500 animate-spin" />}
            {caseData && (
              <div className="flex items-center gap-2 px-3 py-1.5 bg-green-100 text-green-700 rounded-lg text-sm">
                <CheckCircle className="w-4 h-4" />
                <span>Case loaded</span>
              </div>
            )}
          </div>
        </div>
        
        {/* Case Context Banner */}
        {caseData && (
          <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-blue-900">
                  Analyzing: {caseData.case_context?.claimant_name || caseData.case_context?.client_name || caseData.case_context?.business_name}
                </p>
                <p className="text-xs text-blue-700 mt-1">
                  {caseData.case_context?.event_type} • {caseData.case_context?.industry} • 
                  Claim: {formatCurrency(caseData.settlement_values?.claim_value || 0)}
                </p>
              </div>
              <button
                onClick={() => {
                  setSelectedCaseId('');
                  setCaseData(null);
                }}
                className="text-xs text-blue-600 hover:text-blue-800"
              >
                Clear
              </button>
            </div>
          </div>
        )}
      </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 mb-6 border-b border-gray-200 overflow-x-auto">
        {[
          { id: 'sensitivity', label: 'Sensitivity', icon: TrendingUp },
          { id: 'historical', label: 'Historical', icon: History },
          { id: 'whatif', label: 'What-If', icon: Lightbulb },
          { id: 'builder', label: 'Strategy Builder', icon: Wrench },
          { id: 'litigation', label: 'Litigation Economics', icon: Scale }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id as any)}
            className={`flex items-center gap-2 px-3 py-3 font-medium transition-colors border-b-2 -mb-px whitespace-nowrap ${
              activeTab === tab.id
                ? 'text-blue-600 border-blue-600'
                : 'text-gray-500 border-transparent hover:text-gray-700'
            }`}
            data-testid={`tab-${tab.id}`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Sensitivity Analysis Tab */}
      {activeTab === 'sensitivity' && (
        <div className="space-y-6">
          {/* Input Form */}
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-green-600" />
              Quick Sensitivity Analysis
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Claim Value *
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                  <input
                    type="number"
                    value={claimValue}
                    onChange={(e) => setClaimValue(e.target.value)}
                    className="w-full pl-8 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="500000"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Perturbation (±%)
                </label>
                <input
                  type="number"
                  value={perturbationPct}
                  onChange={(e) => setPerturbationPct(e.target.value)}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="20"
                  min="5"
                  max="50"
                />
              </div>
              <div className="flex items-end">
                <button
                  onClick={runSensitivityAnalysis}
                  disabled={loading}
                  className="w-full flex items-center justify-center gap-2 px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-all font-medium"
                >
                  {loading ? (
                    <RefreshCw className="w-5 h-5 animate-spin" />
                  ) : (
                    <Play className="w-5 h-5" />
                  )}
                  {loading ? 'Analyzing...' : 'Run Analysis'}
                </button>
              </div>
            </div>
            
            <p className="text-sm text-gray-500 flex items-center gap-1">
              <Info className="w-4 h-4" />
              Uses typical industry ratios to estimate expense components
            </p>
          </div>

          {/* Results */}
          {sensitivityResult && (
            <>
              {/* Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">Base Settlement</p>
                      <p className="text-2xl font-bold text-gray-900">
                        {formatCurrency(sensitivityResult.base_outcome)}
                      </p>
                    </div>
                    <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                      <Target className="w-6 h-6 text-blue-600" />
                    </div>
                  </div>
                </div>
                <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">Variables Analyzed</p>
                      <p className="text-2xl font-bold text-gray-900">
                        {sensitivityResult.tornado_data.length}
                      </p>
                    </div>
                    <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                      <Layers className="w-6 h-6 text-green-600" />
                    </div>
                  </div>
                </div>
                <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">Max Swing</p>
                      <p className="text-2xl font-bold text-orange-600">
                        {formatCurrency(sensitivityResult.visualization_data?.max_swing || 0)}
                      </p>
                    </div>
                    <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
                      <TrendingUp className="w-6 h-6 text-orange-600" />
                    </div>
                  </div>
                </div>
                <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">Insights</p>
                      <p className="text-2xl font-bold text-blue-600">
                        {sensitivityResult.insights.length}
                      </p>
                    </div>
                    <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                      <Zap className="w-6 h-6 text-blue-600" />
                    </div>
                  </div>
                </div>
              </div>

              {/* Tornado Chart */}
              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-blue-600" />
                  Tornado Chart - Variable Impact
                </h3>
                <p className="text-sm text-gray-500 mb-4">
                  Shows how ±{perturbationPct}% changes in each variable affect the settlement amount
                </p>
                {renderTornadoChart(
                  sensitivityResult.tornado_data,
                  sensitivityResult.base_outcome,
                  sensitivityResult.visualization_data?.max_swing || sensitivityResult.tornado_data[0]?.swing || 100000
                )}
              </div>

              {/* Top Drivers */}
              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Target className="w-5 h-5 text-green-600" />
                  Top Value Drivers
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {sensitivityResult.top_drivers.map((driver, idx) => (
                    <div key={idx} className="p-4 border border-gray-200 rounded-lg hover:border-blue-200 transition-colors">
                      <div className="flex items-start gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                          driver.rank === 1 ? 'bg-yellow-100 text-yellow-700' :
                          driver.rank === 2 ? 'bg-gray-200 text-gray-600' :
                          'bg-orange-100 text-orange-600'
                        }`}>
                          <span className="font-bold text-sm">#{driver.rank}</span>
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center justify-between">
                            <p className="font-medium text-gray-900">{driver.variable}</p>
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getImpactColor(driver.impact_assessment)}`}>
                              {driver.impact_assessment}
                            </span>
                          </div>
                          <div className="flex items-center gap-4 mt-1 text-sm">
                            <span className="text-gray-500">
                              Swing: <span className="font-medium text-gray-900">{formatCurrency(driver.swing)}</span>
                            </span>
                            <span className="text-gray-500">
                              Elasticity: <span className="font-medium text-gray-900">{driver.elasticity.toFixed(2)}</span>
                            </span>
                          </div>
                          <p className="text-sm text-gray-600 mt-2">{driver.recommendation}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Insights */}
              {sensitivityResult.insights.length > 0 && (
                <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <Zap className="w-5 h-5 text-yellow-600" />
                    Key Insights
                  </h3>
                  <div className="space-y-3">
                    {sensitivityResult.insights.map((insight, idx) => (
                      <div key={idx} className="p-4 bg-gray-50 rounded-lg">
                        <p className="font-medium text-gray-900 mb-1">{insight.title}</p>
                        <p className="text-sm text-gray-600 mb-2">{insight.description}</p>
                        <p className="text-sm text-blue-600 flex items-center gap-1">
                          <ChevronRight className="w-4 h-4" />
                          {insight.recommendation}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Interaction Effects */}
              {sensitivityResult.interaction_effects.length > 0 && (
                <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <Layers className="w-5 h-5 text-blue-600" />
                    Interaction Effects
                  </h3>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-gray-200">
                          <th className="text-left py-2 px-3 text-sm font-medium text-gray-500">Variable 1</th>
                          <th className="text-left py-2 px-3 text-sm font-medium text-gray-500">Variable 2</th>
                          <th className="text-right py-2 px-3 text-sm font-medium text-gray-500">Combined Effect</th>
                          <th className="text-right py-2 px-3 text-sm font-medium text-gray-500">Individual Sum</th>
                          <th className="text-center py-2 px-3 text-sm font-medium text-gray-500">Type</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sensitivityResult.interaction_effects.slice(0, 5).map((effect, idx) => (
                          <tr key={idx} className="border-b border-gray-100">
                            <td className="py-2 px-3 text-sm">{effect.variable_1}</td>
                            <td className="py-2 px-3 text-sm">{effect.variable_2}</td>
                            <td className="py-2 px-3 text-sm text-right font-medium">
                              {formatCurrency(effect.combined_effect)}
                            </td>
                            <td className="py-2 px-3 text-sm text-right">
                              {formatCurrency(effect.individual_sum)}
                            </td>
                            <td className="py-2 px-3 text-center">
                              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                effect.synergy_type === 'amplifying' ? 'bg-green-100 text-green-700' :
                                effect.synergy_type === 'dampening' ? 'bg-orange-100 text-orange-700' :
                                'bg-gray-100 text-gray-600'
                              }`}>
                                {effect.synergy_type}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Historical Comparison Tab */}
      {activeTab === 'historical' && (
        <div className="space-y-6">
          {/* Input Form */}
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <History className="w-5 h-5 text-blue-600" />
              Compare Against Historical Settlements
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Claim Value *
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                  <input
                    type="number"
                    value={historicalClaim}
                    onChange={(e) => setHistoricalClaim(e.target.value)}
                    className="w-full pl-8 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="500000"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Settlement (optional)
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                  <input
                    type="number"
                    value={settlementValue}
                    onChange={(e) => setSettlementValue(e.target.value)}
                    className="w-full pl-8 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="375000"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Industry
                </label>
                <select
                  value={industry}
                  onChange={(e) => setIndustry(e.target.value)}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="general">General</option>
                  <option value="restaurant">Restaurant</option>
                  <option value="retail">Retail</option>
                  <option value="manufacturing">Manufacturing</option>
                  <option value="hospitality">Hospitality</option>
                  <option value="healthcare">Healthcare</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Event Type
                </label>
                <select
                  value={eventType}
                  onChange={(e) => setEventType(e.target.value)}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="general">General</option>
                  <option value="fire">Fire</option>
                  <option value="flood">Flood</option>
                  <option value="hurricane">Hurricane</option>
                  <option value="earthquake">Earthquake</option>
                  <option value="cyberattack">Cyber Attack</option>
                  <option value="covid">COVID</option>
                </select>
              </div>
              <div className="flex items-end">
                <button
                  onClick={runHistoricalComparison}
                  disabled={loading}
                  className="w-full flex items-center justify-center gap-2 px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-all font-medium"
                >
                  {loading ? (
                    <RefreshCw className="w-5 h-5 animate-spin" />
                  ) : (
                    <Play className="w-5 h-5" />
                  )}
                  Compare
                </button>
              </div>
            </div>
          </div>

          {/* Results */}
          {historicalResult && (
            <>
              {/* Position Card */}
              {historicalResult.current_position.percentile_rank !== null && (
                <div className={`rounded-xl p-6 border-2 ${getPositionColor(historicalResult.current_position.position_label)}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-lg font-semibold">
                        Your Position: {historicalResult.current_position.position_label}
                      </p>
                      <p className="text-sm mt-1 opacity-80">
                        {historicalResult.current_position.position_description}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-3xl font-bold">
                        {historicalResult.current_position.percentile_rank?.toFixed(0)}th
                      </p>
                      <p className="text-sm opacity-80">percentile</p>
                    </div>
                  </div>
                  <div className="mt-4 h-4 bg-white/50 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-current rounded-full transition-all"
                      style={{ width: `${historicalResult.current_position.percentile_rank}%` }}
                    />
                  </div>
                  <div className="flex justify-between mt-1 text-xs opacity-70">
                    <span>0%</span>
                    <span>25%</span>
                    <span>50%</span>
                    <span>75%</span>
                    <span>100%</span>
                  </div>
                </div>
              )}

              {/* Benchmark Stats */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
                  <p className="text-sm text-gray-500">Your Recovery Rate</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {historicalResult.current_position.recovery_rate?.toFixed(1) || '--'}%
                  </p>
                </div>
                <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
                  <p className="text-sm text-gray-500">Industry Median</p>
                  <p className="text-2xl font-bold text-blue-600">
                    {historicalResult.benchmarks.adjusted.median_recovery_rate.toFixed(1)}%
                  </p>
                </div>
                <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
                  <p className="text-sm text-gray-500">75th Percentile</p>
                  <p className="text-2xl font-bold text-green-600">
                    {historicalResult.benchmarks.adjusted.p75_recovery_rate.toFixed(1)}%
                  </p>
                </div>
                <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
                  <p className="text-sm text-gray-500">Sample Size</p>
                  <p className="text-2xl font-bold text-gray-700">
                    {historicalResult.benchmarks.sample_size.toLocaleString()}
                  </p>
                </div>
              </div>

              {/* Percentile Values */}
              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Settlement Values by Percentile
                </h3>
                <div className="space-y-3">
                  {[
                    { label: '25th Percentile', key: 'p25', rate: historicalResult.benchmarks.adjusted.p25_recovery_rate },
                    { label: 'Median (50th)', key: 'p50', rate: historicalResult.benchmarks.adjusted.median_recovery_rate },
                    { label: '75th Percentile', key: 'p75', rate: historicalResult.benchmarks.adjusted.p75_recovery_rate },
                    { label: '90th Percentile', key: 'p90', rate: historicalResult.benchmarks.adjusted.p90_recovery_rate }
                  ].map((item) => {
                    const value = historicalResult.percentile_values[item.key as keyof typeof historicalResult.percentile_values];
                    const currentRate = historicalResult.current_position.recovery_rate;
                    const isCurrent = currentRate && Math.abs(currentRate - item.rate) < 3;
                    
                    return (
                      <div key={item.key} className={`flex items-center gap-4 p-3 rounded-lg ${
                        isCurrent ? 'bg-blue-50 border border-blue-200' : 'bg-gray-50'
                      }`}>
                        <div className="w-32 text-sm font-medium text-gray-700">{item.label}</div>
                        <div className="flex-1 h-3 bg-gray-200 rounded-full overflow-hidden">
                          <div 
                            className={`h-full rounded-full ${
                              item.key === 'p90' ? 'bg-green-500' :
                              item.key === 'p75' ? 'bg-blue-500' :
                              item.key === 'p50' ? 'bg-yellow-500' :
                              'bg-orange-400'
                            }`}
                            style={{ width: `${item.rate}%` }}
                          />
                        </div>
                        <div className="w-28 text-right">
                          <span className="font-semibold">{formatCurrency(value)}</span>
                          <span className="text-xs text-gray-500 ml-1">({item.rate.toFixed(0)}%)</span>
                        </div>
                        {isCurrent && (
                          <span className="text-xs bg-blue-600 text-white px-2 py-0.5 rounded-full">YOU</span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Gap Analysis */}
              {historicalResult.current_position.recovery_rate !== null && (
                <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Gap Analysis</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <p className="text-sm text-gray-500">vs. Median</p>
                      <div className="flex items-center gap-2 mt-1">
                        {historicalResult.gap_analysis.vs_median && historicalResult.gap_analysis.vs_median > 0 ? (
                          <ArrowUpRight className="w-5 h-5 text-green-600" />
                        ) : historicalResult.gap_analysis.vs_median && historicalResult.gap_analysis.vs_median < 0 ? (
                          <ArrowDownRight className="w-5 h-5 text-red-600" />
                        ) : (
                          <Minus className="w-5 h-5 text-gray-400" />
                        )}
                        <span className={`text-xl font-bold ${
                          (historicalResult.gap_analysis.vs_median || 0) > 0 ? 'text-green-600' :
                          (historicalResult.gap_analysis.vs_median || 0) < 0 ? 'text-red-600' :
                          'text-gray-600'
                        }`}>
                          {formatCurrency(historicalResult.gap_analysis.vs_median || 0)}
                        </span>
                      </div>
                    </div>
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <p className="text-sm text-gray-500">Gap to 75th %</p>
                      <p className="text-xl font-bold text-blue-600 mt-1">
                        {formatCurrency(historicalResult.gap_analysis.vs_p75 || 0)}
                      </p>
                    </div>
                    <div className="p-4 bg-green-50 rounded-lg">
                      <p className="text-sm text-gray-500">Potential Upside</p>
                      <p className="text-xl font-bold text-green-600 mt-1">
                        {formatCurrency(historicalResult.gap_analysis.potential_upside)}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Recommendations */}
              {historicalResult.recommendations.length > 0 && (
                <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <Zap className="w-5 h-5 text-yellow-600" />
                    Recommendations
                  </h3>
                  <div className="space-y-3">
                    {historicalResult.recommendations.map((rec, idx) => (
                      <div key={idx} className="p-4 bg-gray-50 rounded-lg">
                        <div className="flex items-start gap-3">
                          <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                            <span className="text-blue-600 font-bold text-sm">{rec.priority}</span>
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">{rec.title}</p>
                            <p className="text-sm text-gray-600 mt-1">{rec.recommendation}</p>
                            <p className="text-xs text-gray-500 mt-2">{rec.rationale}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* What-If Modeler Tab */}
      {activeTab === 'whatif' && (
        <div className="space-y-6">
          {/* Input Form */}
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Lightbulb className="w-5 h-5 text-yellow-600" />
              Scenario Planning
            </h3>
            <p className="text-sm text-gray-500 mb-4">
              Compare different approaches to maximize your settlement. Select scenarios to analyze their ROI.
            </p>
            
            <div className="flex gap-4 mb-6">
              <div className="flex-1 max-w-xs">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Base Settlement *
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                  <input
                    type="number"
                    value={whatifClaim}
                    onChange={(e) => setWhatifClaim(e.target.value)}
                    className="w-full pl-8 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="500000"
                    data-testid="whatif-base-settlement"
                  />
                </div>
              </div>
              <div className="flex items-end">
                <button
                  onClick={runWhatIfAnalysis}
                  disabled={loading || selectedTemplates.length === 0}
                  className="flex items-center justify-center gap-2 px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-all font-medium"
                  data-testid="whatif-analyze-btn"
                >
                  {loading ? (
                    <RefreshCw className="w-5 h-5 animate-spin" />
                  ) : (
                    <Calculator className="w-5 h-5" />
                  )}
                  {loading ? 'Analyzing...' : 'Compare Scenarios'}
                </button>
              </div>
            </div>

            {/* Scenario Templates */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {whatifTemplates.map((template) => {
                const IconComponent = getScenarioTypeIcon(template.scenario_type);
                const isSelected = selectedTemplates.includes(template.id);
                
                return (
                  <div
                    key={template.id}
                    onClick={() => toggleTemplate(template.id)}
                    className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                      isSelected
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-blue-300 bg-white'
                    }`}
                    data-testid={`whatif-template-${template.id}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                        isSelected ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-600'
                      }`}>
                        <IconComponent className="w-5 h-5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 truncate">{template.name}</p>
                        <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{template.description}</p>
                      </div>
                      {isSelected && (
                        <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0" />
                      )}
                    </div>
                    <div className="mt-3 flex flex-wrap gap-2 text-xs">
                      <span className="px-2 py-0.5 bg-gray-100 rounded text-gray-600">
                        {formatCurrency(template.total_effort_cost)} cost
                      </span>
                      <span className="px-2 py-0.5 bg-green-100 rounded text-green-700">
                        +{template.total_impact_pct.toFixed(0)}% impact
                      </span>
                      <span className="px-2 py-0.5 bg-blue-100 rounded text-blue-700">
                        {(template.avg_success_probability * 100).toFixed(0)}% success
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
            
            {selectedTemplates.length > 0 && (
              <p className="mt-4 text-sm text-blue-600">
                {selectedTemplates.length} scenario{selectedTemplates.length > 1 ? 's' : ''} selected
              </p>
            )}
          </div>

          {/* What-If Results */}
          {whatifResult && (
            <>
              {/* Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
                  <p className="text-sm text-gray-500">Base Settlement</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {formatCurrency(whatifResult.base_settlement)}
                  </p>
                </div>
                <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
                  <p className="text-sm text-gray-500">Scenarios Analyzed</p>
                  <p className="text-2xl font-bold text-blue-600">
                    {whatifResult.scenarios.length}
                  </p>
                </div>
                <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
                  <p className="text-sm text-gray-500">Best ROI</p>
                  <p className="text-2xl font-bold text-green-600">
                    {whatifResult.comparison_summary.best_roi.roi.toFixed(0)}%
                  </p>
                </div>
                <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
                  <p className="text-sm text-gray-500">Best Net Benefit</p>
                  <p className="text-2xl font-bold text-blue-600">
                    {formatCurrency(whatifResult.comparison_summary.best_net_benefit.benefit)}
                  </p>
                </div>
              </div>

              {/* Recommendation Banner */}
              <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl p-6 text-white">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Award className="w-6 h-6" />
                  </div>
                  <div>
                    <p className="text-blue-100 text-sm font-medium">Recommended Approach</p>
                    <p className="text-xl font-bold mt-1">
                      {whatifResult.comparison_summary.recommended.scenario}
                    </p>
                    <p className="text-blue-100 mt-2 text-sm">
                      {whatifResult.comparison_summary.recommended.rationale}
                    </p>
                  </div>
                </div>
              </div>

              {/* Scenario Comparison */}
              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-blue-600" />
                  Scenario Comparison
                </h3>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Scenario</th>
                        <th className="text-right py-3 px-4 text-sm font-medium text-gray-500">Cost</th>
                        <th className="text-right py-3 px-4 text-sm font-medium text-gray-500">Projected</th>
                        <th className="text-right py-3 px-4 text-sm font-medium text-gray-500">Net Benefit</th>
                        <th className="text-right py-3 px-4 text-sm font-medium text-gray-500">ROI</th>
                        <th className="text-right py-3 px-4 text-sm font-medium text-gray-500">Risk-Adjusted</th>
                      </tr>
                    </thead>
                    <tbody>
                      {whatifResult.scenarios.map((scenario, idx) => {
                        const isRecommended = scenario.name === whatifResult.comparison_summary.recommended.scenario;
                        return (
                          <tr 
                            key={idx} 
                            className={`border-b border-gray-100 ${isRecommended ? 'bg-blue-50' : ''}`}
                          >
                            <td className="py-3 px-4">
                              <div className="flex items-center gap-2">
                                {isRecommended && <Award className="w-4 h-4 text-blue-600" />}
                                <span className="font-medium text-gray-900">{scenario.name}</span>
                              </div>
                              <p className="text-xs text-gray-500">{scenario.modification_count} modifications</p>
                            </td>
                            <td className="py-3 px-4 text-right text-sm text-gray-700">
                              {formatCurrency(scenario.total_effort_cost)}
                            </td>
                            <td className="py-3 px-4 text-right text-sm font-medium text-gray-900">
                              {formatCurrency(scenario.projected_settlement)}
                            </td>
                            <td className="py-3 px-4 text-right">
                              <span className={`font-medium ${scenario.net_benefit > 0 ? 'text-green-600' : 'text-red-600'}`}>
                                {formatCurrency(scenario.net_benefit)}
                              </span>
                            </td>
                            <td className="py-3 px-4 text-right">
                              <span className={`px-2 py-0.5 rounded-full text-sm font-medium ${getRoiColor(scenario.roi)}`}>
                                {scenario.roi.toFixed(0)}%
                              </span>
                            </td>
                            <td className="py-3 px-4 text-right text-sm text-gray-700">
                              {formatCurrency(scenario.risk_adjusted_benefit)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* ROI Breakdown for Recommended */}
              {whatifResult.recommended_breakdown && (
                <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <Calculator className="w-5 h-5 text-green-600" />
                    ROI Breakdown: {whatifResult.recommended_breakdown.scenario_name}
                  </h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <p className="text-sm text-gray-500">Gross Benefit</p>
                      <p className="text-xl font-bold text-gray-900">
                        {formatCurrency(whatifResult.recommended_breakdown.gross_benefit)}
                      </p>
                    </div>
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <p className="text-sm text-gray-500">Total Cost</p>
                      <p className="text-xl font-bold text-red-600">
                        -{formatCurrency(whatifResult.recommended_breakdown.total_cost)}
                      </p>
                    </div>
                    <div className="p-4 bg-green-50 rounded-lg">
                      <p className="text-sm text-gray-500">Net Benefit</p>
                      <p className="text-xl font-bold text-green-600">
                        {formatCurrency(whatifResult.recommended_breakdown.net_benefit)}
                      </p>
                    </div>
                    <div className="p-4 bg-blue-50 rounded-lg">
                      <p className="text-sm text-gray-500">ROI</p>
                      <p className="text-xl font-bold text-blue-600">
                        {whatifResult.recommended_breakdown.roi_percentage.toFixed(0)}%
                      </p>
                    </div>
                  </div>

                  {/* Modification Breakdown */}
                  <h4 className="font-medium text-gray-900 mb-3">Modification Impact</h4>
                  <div className="space-y-2">
                    {whatifResult.recommended_breakdown.modification_breakdown.map((mod, idx) => (
                      <div key={idx} className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg">
                        <div className="flex-1">
                          <p className="text-sm font-medium text-gray-900">{mod.name}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm text-gray-500">
                            Cost: <span className="font-medium">{formatCurrency(mod.effort_cost)}</span>
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm text-gray-500">
                            Impact: <span className="font-medium text-green-600">{formatCurrency(mod.dollar_impact)}</span>
                          </p>
                        </div>
                        <div className="w-20 text-right">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getRoiColor(mod.individual_roi)}`}>
                            {mod.individual_roi.toFixed(0)}% ROI
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Strategy Builder Tab */}
      {activeTab === 'builder' && (
        <div className="space-y-6">
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <h3 className="text-lg font-semibold text-gray-900 mb-2 flex items-center gap-2">
              <Wrench className="w-5 h-5 text-blue-600" />
              Build Your Own Strategy
            </h3>
            <p className="text-sm text-gray-500 mb-4">
              Mix and match individual modifications from any scenario template to create your optimal strategy.
            </p>

            <div className="flex gap-4 mb-6">
              <div className="flex-1 max-w-xs">
                <label className="block text-sm font-medium text-gray-700 mb-1">Base Settlement *</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                  <input
                    type="number"
                    value={builderClaim}
                    onChange={(e) => setBuilderClaim(e.target.value)}
                    className="w-full pl-8 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    data-testid="builder-base-settlement"
                  />
                </div>
              </div>
              <div className="flex items-end">
                <button
                  onClick={runBuildStrategy}
                  disabled={loading || selectedMods.length === 0}
                  className="flex items-center gap-2 px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                  data-testid="builder-analyze-btn"
                >
                  {loading ? <RefreshCw className="w-5 h-5 animate-spin" /> : <Plus className="w-5 h-5" />}
                  Build Strategy ({selectedMods.length})
                </button>
              </div>
            </div>

            {/* Modifications by Category */}
            {Object.entries(modsByCategory).map(([category, mods]) => (
              <div key={category} className="mb-6">
                <h4 className="text-sm font-semibold text-gray-700 mb-3 capitalize flex items-center gap-2">
                  {getScenarioTypeIcon(category) && React.createElement(getScenarioTypeIcon(category), { className: 'w-4 h-4' })}
                  {category.replace(/_/g, ' ')}
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {mods.map((mod: Modification) => {
                    const isSelected = selectedMods.includes(mod.id);
                    return (
                      <div
                        key={mod.id}
                        onClick={() => toggleMod(mod.id)}
                        className={`p-3 rounded-lg border cursor-pointer transition-all ${
                          isSelected ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-blue-300'
                        }`}
                      >
                        <div className="flex items-start gap-2">
                          <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 mt-0.5 ${
                            isSelected ? 'border-blue-500 bg-blue-500' : 'border-gray-300'
                          }`}>
                            {isSelected && <CheckCircle className="w-3 h-3 text-white" />}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900">{mod.name}</p>
                            <p className="text-xs text-gray-500 mt-0.5">{mod.description}</p>
                            <div className="flex flex-wrap gap-1 mt-2">
                              <span className="text-xs px-1.5 py-0.5 bg-gray-100 rounded">{formatCurrency(mod.effort_cost)}</span>
                              <span className="text-xs px-1.5 py-0.5 bg-green-100 text-green-700 rounded">+{mod.settlement_impact_pct}%</span>
                              <span className="text-xs px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded">{(mod.success_probability * 100).toFixed(0)}%</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          {/* Builder Results */}
          {builderResult && builderResult.success && (
            <>
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Custom Strategy Results</h3>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-500">Base Settlement</p>
                  <p className="text-xl font-bold text-gray-900">{formatCurrency(builderResult.scenario.base_settlement)}</p>
                </div>
                <div className="p-4 bg-green-50 rounded-lg">
                  <p className="text-sm text-gray-500">Projected Settlement</p>
                  <p className="text-xl font-bold text-green-600">{formatCurrency(builderResult.scenario.projected_settlement)}</p>
                </div>
                <div className="p-4 bg-blue-50 rounded-lg">
                  <p className="text-sm text-gray-500">Total Cost</p>
                  <p className="text-xl font-bold text-blue-600">{formatCurrency(builderResult.scenario.total_effort_cost)}</p>
                </div>
                <div className="p-4 bg-yellow-50 rounded-lg">
                  <p className="text-sm text-gray-500">ROI</p>
                  <p className="text-xl font-bold text-yellow-600">{builderResult.scenario.roi.toFixed(0)}%</p>
                </div>
              </div>

              <div className="p-4 bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg text-white">
                <p className="text-sm opacity-80">Net Benefit</p>
                <p className="text-2xl font-bold">{formatCurrency(builderResult.scenario.net_benefit)}</p>
                <p className="text-sm opacity-80 mt-1">
                  Risk-adjusted: {formatCurrency(builderResult.scenario.risk_adjusted_benefit)}
                </p>
              </div>
            </div>

            {/* Strategy Builder Calculation Details */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
              <button
                onClick={() => setShowBuilderCalcs(!showBuilderCalcs)}
                className="w-full p-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
                data-testid="show-builder-calcs"
              >
                <div className="flex items-center gap-2">
                  <Code className="w-5 h-5 text-blue-600" />
                  <span className="font-semibold text-gray-900">Show Calculation Details</span>
                  <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded">No AI - Pure Math</span>
                </div>
                {showBuilderCalcs ? <ChevronUp className="w-5 h-5 text-gray-500" /> : <ChevronDown className="w-5 h-5 text-gray-500" />}
              </button>
              
              {showBuilderCalcs && (
                <div className="p-6 border-t border-gray-100 bg-gray-50">
                  <div className="space-y-6 font-mono text-sm">
                    {/* Impact Calculation */}
                    <div>
                      <h5 className="font-semibold text-gray-900 mb-2 font-sans">1. Settlement Impact (with Diminishing Returns)</h5>
                      <div className="bg-white p-4 rounded-lg border border-gray-200">
                        <p className="text-gray-600 mb-2">effective_impact = raw_impact × 0.9^(modification_index)</p>
                        <p className="text-gray-500 text-xs">Each stacked modification has 10% less impact than previous</p>
                        <div className="mt-3 pt-3 border-t border-gray-100">
                          {builderResult.scenario.modifications?.map((mod: any, idx: number) => (
                            <p key={idx} className="text-gray-700">
                              <span className="text-gray-500">Mod {idx + 1}:</span> {mod.settlement_impact_pct}% × 0.9^{idx} = {(mod.settlement_impact_pct * Math.pow(0.9, idx)).toFixed(2)}%
                            </p>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Projected Settlement */}
                    <div>
                      <h5 className="font-semibold text-gray-900 mb-2 font-sans">2. Projected Settlement</h5>
                      <div className="bg-white p-4 rounded-lg border border-gray-200">
                        <p className="text-gray-600 mb-2">projected = base × (1 + total_impact%)</p>
                        <div className="mt-3 pt-3 border-t border-gray-100">
                          <p className="text-gray-700">
                            {formatCurrency(builderResult.scenario.base_settlement)} × (1 + {((builderResult.scenario.projected_settlement / builderResult.scenario.base_settlement - 1) * 100).toFixed(2)}%)
                          </p>
                          <p className="text-gray-700 mt-1">
                            <span className="text-gray-500">= </span>
                            <span className="font-bold">{formatCurrency(builderResult.scenario.projected_settlement)}</span>
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* ROI Calculation */}
                    <div>
                      <h5 className="font-semibold text-gray-900 mb-2 font-sans">3. ROI Calculation</h5>
                      <div className="bg-white p-4 rounded-lg border border-gray-200">
                        <p className="text-gray-600 mb-2">ROI = ((projected - base - cost) / cost) × 100</p>
                        <div className="mt-3 pt-3 border-t border-gray-100">
                          <p className="text-gray-700">
                            Gross Benefit = {formatCurrency(builderResult.scenario.projected_settlement)} - {formatCurrency(builderResult.scenario.base_settlement)} = {formatCurrency(builderResult.scenario.projected_settlement - builderResult.scenario.base_settlement)}
                          </p>
                          <p className="text-gray-700 mt-1">
                            Net Benefit = {formatCurrency(builderResult.scenario.projected_settlement - builderResult.scenario.base_settlement)} - {formatCurrency(builderResult.scenario.total_effort_cost)} = {formatCurrency(builderResult.scenario.net_benefit)}
                          </p>
                          <p className="text-gray-700 mt-1">
                            ROI = ({formatCurrency(builderResult.scenario.net_benefit)} / {formatCurrency(builderResult.scenario.total_effort_cost)}) × 100 = <span className="font-bold">{builderResult.scenario.roi.toFixed(1)}%</span>
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Risk-Adjusted Benefit */}
                    <div>
                      <h5 className="font-semibold text-gray-900 mb-2 font-sans">4. Risk-Adjusted Benefit</h5>
                      <div className="bg-white p-4 rounded-lg border border-gray-200">
                        <p className="text-gray-600 mb-2">risk_adjusted = net_benefit × combined_success_probability</p>
                        <div className="mt-3 pt-3 border-t border-gray-100">
                          <p className="text-gray-700">
                            Combined Success = {builderResult.scenario.modifications?.map((m: any) => `${(m.success_probability * 100).toFixed(0)}%`).join(' × ')}
                          </p>
                          <p className="text-gray-700 mt-1">
                            Risk-Adjusted = {formatCurrency(builderResult.scenario.net_benefit)} × success_rate = <span className="font-bold">{formatCurrency(builderResult.scenario.risk_adjusted_benefit)}</span>
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Disclaimer */}
                    <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                      <p className="text-blue-800 text-xs font-sans">
                        <strong>Note:</strong> These calculations use deterministic mathematical formulas with no AI/LLM involvement. 
                        Diminishing returns (0.9^n) prevent unrealistic stacking. Success probabilities are based on industry data for similar efforts.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
            </>
          )}
        </div>
      )}

      {/* Litigation Economics Tab */}
      {activeTab === 'litigation' && (
        <div className="space-y-6">
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <h3 className="text-lg font-semibold text-gray-900 mb-2 flex items-center gap-2">
              <Scale className="w-5 h-5 text-blue-600" />
              Settlement vs Litigation Analysis
            </h3>
            <p className="text-sm text-gray-500 mb-4">
              Compare accepting a settlement offer against litigating. Uses NPV to account for time value and risk.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Current Offer *</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                  <input
                    type="number"
                    value={currentOffer}
                    onChange={(e) => setCurrentOffer(e.target.value)}
                    className="w-full pl-8 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    data-testid="litigation-offer"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Full Claim Value *</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                  <input
                    type="number"
                    value={litigationClaim}
                    onChange={(e) => setLitigationClaim(e.target.value)}
                    className="w-full pl-8 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    data-testid="litigation-claim"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Case Complexity</label>
                <select
                  value={complexity}
                  onChange={(e) => setComplexity(e.target.value)}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="extreme">Extreme</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Documentation Strength</label>
                <select
                  value={docStrength}
                  onChange={(e) => setDocStrength(e.target.value)}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="weak">Weak</option>
                  <option value="medium">Medium</option>
                  <option value="strong">Strong</option>
                  <option value="very_strong">Very Strong</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Fee Arrangement</label>
                <select
                  value={feeArrangement}
                  onChange={(e) => setFeeArrangement(e.target.value)}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="contingency">Contingency (33-40%)</option>
                  <option value="hourly">Hourly</option>
                  <option value="hybrid">Hybrid</option>
                </select>
              </div>
              <div className="flex items-end">
                <button
                  onClick={runLitigationAnalysis}
                  disabled={loading}
                  className="w-full flex items-center justify-center gap-2 px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                  data-testid="litigation-analyze-btn"
                >
                  {loading ? <RefreshCw className="w-5 h-5 animate-spin" /> : <Scale className="w-5 h-5" />}
                  Analyze
                </button>
              </div>
            </div>
          </div>

          {/* Litigation Results */}
          {litigationResult && (
            <>
              {/* Recommendation Banner */}
              <div className={`rounded-xl p-6 ${getRecommendationStyle(litigationResult.recommendation.action)}`}>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                    {litigationResult.recommendation.action === 'accept' && <CheckCircle className="w-6 h-6" />}
                    {litigationResult.recommendation.action === 'negotiate' && <Target className="w-6 h-6" />}
                    {litigationResult.recommendation.action === 'reject_litigate' && <Scale className="w-6 h-6" />}
                  </div>
                  <div>
                    <p className="text-sm opacity-80 font-medium">Recommendation</p>
                    <p className="text-2xl font-bold capitalize">{litigationResult.recommendation.action.replace('_', ' ')}</p>
                    <p className="text-sm opacity-90 mt-2">{litigationResult.recommendation.rationale}</p>
                    <p className="text-xs opacity-70 mt-1">Confidence: {litigationResult.recommendation.confidence}</p>
                  </div>
                </div>
              </div>

              {/* Path Comparison */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Settlement Path */}
                <div className="bg-white rounded-xl p-6 shadow-sm border border-green-200">
                  <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    Settlement Path
                  </h4>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Offer Amount</span>
                      <span className="font-semibold">{formatCurrency(litigationResult.settlement_path.offer_amount)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Net Present Value</span>
                      <span className="font-semibold text-green-600">{formatCurrency(litigationResult.settlement_path.npv)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Time to Payment</span>
                      <span className="font-semibold">Immediate</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Certainty</span>
                      <span className="font-semibold text-green-600">100%</span>
                    </div>
                  </div>
                </div>

                {/* Litigation Path */}
                <div className="bg-white rounded-xl p-6 shadow-sm border border-orange-200">
                  <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <Scale className="w-5 h-5 text-orange-600" />
                    Litigation Path
                  </h4>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Expected Value (NPV)</span>
                      <span className="font-semibold text-orange-600">{formatCurrency(litigationResult.litigation_path.expected_value_npv)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Win Probability</span>
                      <span className="font-semibold">{(litigationResult.litigation_path.win_probability * 100).toFixed(0)}%</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Total Litigation Cost</span>
                      <span className="font-semibold text-red-600">-{formatCurrency(litigationResult.litigation_path.total_litigation_cost)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Time to Resolution</span>
                      <span className="font-semibold">{litigationResult.litigation_path.time_to_resolution_months} months</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Key Metrics */}
              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <h4 className="text-lg font-semibold text-gray-900 mb-4">Key Metrics</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="p-4 bg-gray-50 rounded-lg text-center">
                    <p className="text-sm text-gray-500">Settlement Advantage</p>
                    <p className={`text-xl font-bold ${litigationResult.comparison.settlement_advantage > 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {formatCurrency(litigationResult.comparison.settlement_advantage)}
                    </p>
                  </div>
                  <div className="p-4 bg-gray-50 rounded-lg text-center">
                    <p className="text-sm text-gray-500">Advantage %</p>
                    <p className={`text-xl font-bold ${litigationResult.comparison.advantage_percentage > 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {litigationResult.comparison.advantage_percentage.toFixed(0)}%
                    </p>
                  </div>
                  <div className="p-4 bg-blue-50 rounded-lg text-center">
                    <p className="text-sm text-gray-500">Break-Even Settlement</p>
                    <p className="text-xl font-bold text-blue-600">{formatCurrency(litigationResult.comparison.break_even_settlement)}</p>
                  </div>
                  <div className="p-4 bg-yellow-50 rounded-lg text-center">
                    <p className="text-sm text-gray-500">Recommended Counter</p>
                    <p className="text-xl font-bold text-yellow-600">{formatCurrency(litigationResult.comparison.recommended_counter_offer)}</p>
                  </div>
                </div>
              </div>

              {/* Cost Breakdown */}
              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <h4 className="text-lg font-semibold text-gray-900 mb-4">Litigation Cost Breakdown</h4>
                <div className="space-y-2">
                  {Object.entries(litigationResult.litigation_cost_breakdown).filter(([k]) => k !== 'total').map(([key, value]) => (
                    <div key={key} className="flex justify-between items-center p-2 bg-gray-50 rounded">
                      <span className="text-sm text-gray-600 capitalize">{key.replace(/_/g, ' ')}</span>
                      <span className="font-medium">{formatCurrency(value as number)}</span>
                    </div>
                  ))}
                  <div className="flex justify-between items-center p-3 bg-red-50 rounded-lg mt-2">
                    <span className="font-semibold text-gray-900">Total Estimated Cost</span>
                    <span className="font-bold text-red-600">{formatCurrency(litigationResult.litigation_cost_breakdown.total)}</span>
                  </div>
                </div>
              </div>

              {/* Calculation Details - Collapsible */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                <button
                  onClick={() => setShowLitigationCalcs(!showLitigationCalcs)}
                  className="w-full p-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
                  data-testid="show-litigation-calcs"
                >
                  <div className="flex items-center gap-2">
                    <Code className="w-5 h-5 text-blue-600" />
                    <span className="font-semibold text-gray-900">Show Calculation Details</span>
                    <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded">No AI - Pure Math</span>
                  </div>
                  {showLitigationCalcs ? <ChevronUp className="w-5 h-5 text-gray-500" /> : <ChevronDown className="w-5 h-5 text-gray-500" />}
                </button>
                
                {showLitigationCalcs && (
                  <div className="p-6 border-t border-gray-100 bg-gray-50">
                    <div className="space-y-6 font-mono text-sm">
                      {/* NPV Formula */}
                      <div>
                        <h5 className="font-semibold text-gray-900 mb-2 font-sans">1. Net Present Value (NPV) Formula</h5>
                        <div className="bg-white p-4 rounded-lg border border-gray-200">
                          <p className="text-gray-600 mb-2">PV = FV / (1 + r)^n</p>
                          <p className="text-gray-500 text-xs">where r = 8% annual discount rate, n = years</p>
                          <div className="mt-3 pt-3 border-t border-gray-100">
                            <p className="text-gray-700">
                              <span className="text-gray-500">Litigation NPV =</span> {formatCurrency(litigationResult.litigation_path.expected_value_npv)}
                            </p>
                            <p className="text-gray-500 text-xs mt-1">
                              Future value discounted over {litigationResult.litigation_path.time_to_resolution_months} months ({(litigationResult.litigation_path.time_to_resolution_months / 12).toFixed(2)} years)
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Expected Value Formula */}
                      <div>
                        <h5 className="font-semibold text-gray-900 mb-2 font-sans">2. Expected Value Calculation</h5>
                        <div className="bg-white p-4 rounded-lg border border-gray-200">
                          <p className="text-gray-600 mb-2">EV = Σ(outcome × probability)</p>
                          <div className="mt-3 pt-3 border-t border-gray-100 space-y-2">
                            <p className="text-gray-700">
                              <span className="text-green-600">Win (no appeal):</span> {(litigationResult.litigation_path.win_probability * 0.7 * 100).toFixed(1)}% × net_value
                            </p>
                            <p className="text-gray-700">
                              <span className="text-yellow-600">Win (appeal upheld):</span> {(litigationResult.litigation_path.win_probability * 0.3 * 0.65 * 100).toFixed(1)}% × net_value
                            </p>
                            <p className="text-gray-700">
                              <span className="text-orange-600">Win (appeal reversed):</span> {(litigationResult.litigation_path.win_probability * 0.3 * 0.35 * 100).toFixed(1)}% × (-costs)
                            </p>
                            <p className="text-gray-700">
                              <span className="text-red-600">Lose at trial:</span> {((1 - litigationResult.litigation_path.win_probability) * 100).toFixed(1)}% × (-costs)
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Win Probability */}
                      <div>
                        <h5 className="font-semibold text-gray-900 mb-2 font-sans">3. Win Probability Estimation</h5>
                        <div className="bg-white p-4 rounded-lg border border-gray-200">
                          <p className="text-gray-600 mb-2">Base rate: 55% (plaintiff win rate at trial)</p>
                          <p className="text-gray-600">Adjusted by: documentation strength, policy clarity, causation</p>
                          <div className="mt-3 pt-3 border-t border-gray-100">
                            <p className="text-gray-700">
                              <span className="text-gray-500">Final Win Probability =</span> <span className="font-bold">{(litigationResult.litigation_path.win_probability * 100).toFixed(1)}%</span>
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Break-Even */}
                      <div>
                        <h5 className="font-semibold text-gray-900 mb-2 font-sans">4. Break-Even Settlement</h5>
                        <div className="bg-white p-4 rounded-lg border border-gray-200">
                          <p className="text-gray-600 mb-2">Break-Even = Expected Litigation NPV</p>
                          <p className="text-gray-500 text-xs">Any settlement above this is better than litigating</p>
                          <div className="mt-3 pt-3 border-t border-gray-100">
                            <p className="text-gray-700">
                              <span className="text-gray-500">Break-Even =</span> <span className="font-bold">{formatCurrency(litigationResult.comparison.break_even_settlement)}</span>
                            </p>
                            <p className="text-gray-700 mt-1">
                              <span className="text-gray-500">Current Offer =</span> {formatCurrency(litigationResult.settlement_path.offer_amount)}
                            </p>
                            <p className="text-gray-700 mt-1">
                              <span className="text-gray-500">Difference =</span> 
                              <span className={litigationResult.comparison.settlement_advantage > 0 ? 'text-green-600' : 'text-red-600'}>
                                {' '}{formatCurrency(litigationResult.comparison.settlement_advantage)} ({litigationResult.comparison.advantage_percentage.toFixed(0)}%)
                              </span>
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Disclaimer */}
                      <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                        <p className="text-blue-800 text-xs font-sans">
                          <strong>Note:</strong> These calculations use deterministic mathematical formulas with no AI/LLM involvement. 
                          Same inputs will always produce the same outputs. Default assumptions: 8% discount rate, 30% appeal risk, 
                          33-40% contingency fees. Adjust inputs for case-specific analysis.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
